import { Alert, Button, Drawer, Modal, Space, Table, Typography, Upload, UploadProps } from "antd";
import { FileExcelOutlined, UploadOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { openNotification } from "src/globals/Components/Notification/Notificaction";
import {
    ActivityDataImportCommitResult,
    ActivityDataImportPreviewResult,
    ActivityDataImportValidationIssue,
} from "src/store/activity/types";

const MAX_XLSX_BYTES = 10 * 1024 * 1024;

type ImportPhase =
    | "idle"
    | "downloading"
    | "fileSelected"
    | "previewing"
    | "previewValid"
    | "previewInvalid"
    | "confirming"
    | "importing"
    | "success"
    | "requestError";

interface Props {
    open: boolean;
    geiInventoryId: string;
    onClose: () => void;
    onImported: () => void | Promise<void>;
    downloadTemplate: (geiInventoryId: string) => Promise<{ blob: Blob; fileName: string } | undefined>;
    previewImport: (geiInventoryId: string, file: File) => Promise<ActivityDataImportPreviewResult | undefined>;
    commitImport: (
        geiInventoryId: string,
        file: File,
        confirmUpdates: boolean
    ) => Promise<ActivityDataImportCommitResult | undefined>;
}

function triggerBlobDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

const ActivityDataExcelImportDrawer: React.FC<Props> = ({
    open,
    geiInventoryId,
    onClose,
    onImported,
    downloadTemplate,
    previewImport,
    commitImport,
}) => {
    const [phase, setPhase] = useState<ImportPhase>("idle");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<ActivityDataImportPreviewResult | null>(null);
    const [commitResult, setCommitResult] = useState<ActivityDataImportCommitResult | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const busy = phase === "downloading" || phase === "previewing" || phase === "importing";

    const resetState = () => {
        setPhase("idle");
        setSelectedFile(null);
        setPreview(null);
        setCommitResult(null);
        setConfirmOpen(false);
    };

    const handleClose = () => {
        if (busy) return;
        resetState();
        onClose();
    };

    const handleDownload = async () => {
        setPhase("downloading");
        const result = await downloadTemplate(geiInventoryId);
        if (!result?.blob) {
            setPhase("requestError");
            return;
        }
        triggerBlobDownload(result.blob, result.fileName || "plantilla-actividad-t06.xlsx");
        openNotification("Plantilla", "Descarga iniciada");
        setPhase(selectedFile ? "fileSelected" : "idle");
    };

    const uploadProps: UploadProps = {
        maxCount: 1,
        accept: ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        showUploadList: false,
        beforeUpload: (file) => {
            const nameOk = file.name.toLowerCase().endsWith(".xlsx");
            if (!nameOk) {
                openNotification("Advertencia", `${file.name} debe ser un archivo .xlsx`);
                return Upload.LIST_IGNORE;
            }
            if (file.size > MAX_XLSX_BYTES) {
                openNotification("Advertencia", "El archivo supera el límite de 10 MB");
                return Upload.LIST_IGNORE;
            }
            setSelectedFile(file);
            setPreview(null);
            setCommitResult(null);
            setPhase("fileSelected");
            return false;
        },
    };

    const clearFile = () => {
        if (busy) return;
        setSelectedFile(null);
        setPreview(null);
        setCommitResult(null);
        setPhase("idle");
    };

    const runPreview = async () => {
        if (!selectedFile || busy) return;
        setPhase("previewing");
        setCommitResult(null);
        const result = await previewImport(geiInventoryId, selectedFile);
        if (!result) {
            setPhase("requestError");
            return;
        }
        setPreview(result);
        const errors = result.errors?.length ?? 0;
        setPhase(errors > 0 || result.isValid === false ? "previewInvalid" : "previewValid");
    };

    const applyImportResponse = async (result: ActivityDataImportCommitResult) => {
        setCommitResult(result);
        if (result.confirmationRequired) {
            setPreview({
                isValid: result.isValid,
                confirmationRequired: true,
                errors: result.errors,
                warnings: result.warnings,
                summary: result.previewSummary || undefined,
            });
            setConfirmOpen(true);
            setPhase("confirming");
            return;
        }
        if (result.success) {
            setPhase("success");
            openNotification(
                "Carga masiva",
                `Importación completada. Creados: ${result.summary?.created ?? 0}, actualizados: ${result.summary?.updated ?? 0}, ignorados: ${result.summary?.ignored ?? 0}.`
            );
            await onImported();
            return;
        }
        // validation failed on re-parse
        setPreview({
            isValid: result.isValid,
            confirmationRequired: result.confirmationRequired,
            errors: result.errors,
            warnings: result.warnings,
            summary: result.previewSummary || undefined,
        });
        setPhase((result.errors?.length ?? 0) > 0 ? "previewInvalid" : "previewValid");
        openNotification("Advertencia", "La validación del servidor impidió la importación");
    };

    const runImport = async (confirmUpdates: boolean) => {
        if (!selectedFile || busy) return;
        setConfirmOpen(false);
        setPhase("importing");
        const result = await commitImport(geiInventoryId, selectedFile, confirmUpdates);
        if (!result) {
            setPhase("requestError");
            return;
        }
        await applyImportResponse(result);
    };

    const requestImport = () => {
        if (!selectedFile || phase !== "previewValid" || busy) return;
        const updateRows = preview?.summary?.updateRows ?? 0;
        if (updateRows > 0) {
            setConfirmOpen(true);
            setPhase("confirming");
            return;
        }
        void runImport(false);
    };

    const summary = preview?.summary;
    const errors = preview?.errors ?? [];
    const warnings = preview?.warnings ?? [];
    const updateRows = summary?.updateRows ?? 0;

    const errorColumns = useMemo(
        () => [
            { title: "Hoja", dataIndex: "sheet", key: "sheet", width: 120 },
            { title: "Fila", dataIndex: "row", key: "row", width: 70 },
            { title: "Campo", dataIndex: "field", key: "field", width: 120 },
            { title: "Valor", dataIndex: "value", key: "value", width: 120, ellipsis: true },
            { title: "Motivo", dataIndex: "reason", key: "reason", ellipsis: true },
        ],
        []
    );

    const warningColumns = useMemo(
        () => [
            { title: "Fila", dataIndex: "row", key: "row", width: 70 },
            { title: "Mes", dataIndex: "field", key: "field", width: 90 },
            { title: "Valor", dataIndex: "value", key: "value", width: 100 },
            {
                title: "Promedio ref.",
                dataIndex: "comparisonAverage",
                key: "comparisonAverage",
                width: 110,
                render: (v: number | null | undefined) => (v == null ? "—" : v),
            },
            {
                title: "Desviación %",
                dataIndex: "deviationPercentage",
                key: "deviationPercentage",
                width: 110,
                render: (v: number | null | undefined) => (v == null ? "—" : v),
            },
            { title: "Motivo", dataIndex: "reason", key: "reason", ellipsis: true },
        ],
        []
    );

    return (
        <>
            <Drawer
                title="Carga masiva Excel"
                open={open}
                onClose={handleClose}
                width={720}
                destroyOnClose
                footer={null}
            >
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Descarga la plantilla oficial, completa solo los valores mensuales y observaciones.
                        No modifiques las columnas de solo lectura (ID de asignación, establecimiento, fuente,
                        flujo, unidad, total anual). El total anual se calcula automáticamente.
                    </Typography.Paragraph>

                    <Button
                        icon={<FileExcelOutlined />}
                        onClick={handleDownload}
                        loading={phase === "downloading"}
                        disabled={busy && phase !== "downloading"}
                    >
                        Descargar plantilla
                    </Button>

                    <Space wrap>
                        <Upload {...uploadProps} disabled={busy}>
                            <Button icon={<UploadOutlined />} disabled={busy}>
                                Seleccionar archivo .xlsx
                            </Button>
                        </Upload>
                        {selectedFile && (
                            <>
                                <Typography.Text>
                                    Archivo: <strong>{selectedFile.name}</strong>
                                </Typography.Text>
                                <Button type="link" onClick={clearFile} disabled={busy}>
                                    Quitar
                                </Button>
                            </>
                        )}
                    </Space>

                    <Space>
                        <Button
                            type="default"
                            onClick={runPreview}
                            disabled={!selectedFile || busy}
                            loading={phase === "previewing"}
                        >
                            Validar archivo
                        </Button>
                        <Button
                            type="primary"
                            onClick={requestImport}
                            disabled={phase !== "previewValid" || !selectedFile || busy}
                            loading={phase === "importing"}
                        >
                            Confirmar importación
                        </Button>
                    </Space>

                    {preview && (
                        <Alert
                            type={(errors.length > 0) ? "error" : (warnings.length > 0 ? "warning" : "success")}
                            showIcon
                            message="Resumen de validación"
                            description={
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    <li>Filas válidas: {summary?.validRows ?? 0}</li>
                                    <li>Nuevos registros: {summary?.createRows ?? 0}</li>
                                    <li>Registros a actualizar: {summary?.updateRows ?? 0}</li>
                                    <li>Filas ignoradas: {summary?.ignoredRows ?? 0}</li>
                                    <li>Errores: {summary?.errorCount ?? errors.length}</li>
                                    <li>Advertencias: {summary?.warningCount ?? warnings.length}</li>
                                </ul>
                            }
                        />
                    )}

                    {errors.length > 0 && (
                        <>
                            <Typography.Title level={5}>Errores ({errors.length})</Typography.Title>
                            <Typography.Paragraph type="secondary">
                                Corrige estos errores en el archivo antes de importar.
                            </Typography.Paragraph>
                            <Table
                                size="small"
                                rowKey={(_, i) => `err-${i}`}
                                columns={errorColumns}
                                dataSource={errors as ActivityDataImportValidationIssue[]}
                                pagination={false}
                                scroll={{ y: 220, x: 600 }}
                            />
                        </>
                    )}

                    {warnings.length > 0 && (
                        <>
                            <Typography.Title level={5}>Advertencias ±15% ({warnings.length})</Typography.Title>
                            <Alert
                                type="warning"
                                showIcon
                                message="Estos valores presentan una variación igual o superior al 15% respecto del promedio de los demás meses informados. Puedes continuar con la carga si los datos son correctos."
                            />
                            <Table
                                size="small"
                                rowKey={(_, i) => `warn-${i}`}
                                columns={warningColumns}
                                dataSource={warnings as ActivityDataImportValidationIssue[]}
                                pagination={false}
                                scroll={{ y: 220, x: 700 }}
                            />
                        </>
                    )}

                    {phase === "success" && commitResult?.summary && (
                        <Alert
                            type="success"
                            showIcon
                            message="Importación exitosa"
                            description={
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    <li>Creados: {commitResult.summary.created ?? 0}</li>
                                    <li>Actualizados: {commitResult.summary.updated ?? 0}</li>
                                    <li>Ignorados: {commitResult.summary.ignored ?? 0}</li>
                                    <li>Advertencias: {commitResult.summary.warnings ?? 0}</li>
                                </ul>
                            }
                        />
                    )}

                    {phase === "requestError" && (
                        <Alert type="error" showIcon message="No se pudo completar la solicitud. Revisa el archivo o intenta nuevamente." />
                    )}
                </Space>
            </Drawer>

            <Modal
                open={confirmOpen}
                title="Confirmar actualización"
                okText="Confirmar actualización"
                cancelText="Cancelar"
                confirmLoading={phase === "importing"}
                onCancel={() => {
                    if (phase === "importing") return;
                    setConfirmOpen(false);
                    setPhase(errors.length > 0 ? "previewInvalid" : "previewValid");
                }}
                onOk={() => void runImport(true)}
            >
                <p>
                    Esta carga actualizará {updateRows} registros existentes. Los valores mensuales y observaciones
                    actuales serán reemplazados por los contenidos en el archivo.
                </p>
            </Modal>
        </>
    );
};

export default ActivityDataExcelImportDrawer;
